import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig, isAxiosError } from 'axios';
import {
  ApiResponse,
  StaffUser,
  LoginCredentials,
  Booking,
  DashboardStats,
  CreateBookingForm,
  UpdateBookingStatusForm,
  UpdateBookingDetailsForm,
  Location,
  Court,
  TimeSlot,
  Payment,
  StaffNotification,
  SchedulingOverview,
  ProgrammeCalendarResponse,
  Programme,
  ProgrammeSession,
  ProgrammeEnrollment,
  PrivateLesson,
  PrivateLessonParticipant,
} from '@/types';
import { Role, Permission } from '@/types/permissions';
import toast from 'react-hot-toast';
import i18n from '../i18n';
import { logger } from '@/utils/logger';

interface PaginationInfo {
  current_page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

type UnknownRecord = Record<string, unknown>;
type AppAxiosConfig = AxiosRequestConfig & { suppressErrorToast?: boolean };

const isLocalHostname = (hostname: string) =>
  hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';

const isNexcaveHostname = (hostname: string) =>
  hostname === 'nexcave.com' || hostname.endsWith('.nexcave.com');

const isDefaultWebPort = (protocol: string, port: string) =>
  !port || (protocol === 'https:' && port === '443') || (protocol === 'http:' && port === '80');

const isLoopbackApiUrl = (url: string) =>
  /^https?:\/\/(localhost|127\.0\.0\.1|::1)(:\d+)?/i.test(url);

// API基础配置 - 动态判断环境
const getApiBaseUrl = () => {
  const envBaseUrl = (import.meta.env && import.meta.env.VITE_API_BASE_URL) || '';
  if (envBaseUrl) {
    if (
      import.meta.env.PROD &&
      typeof window !== 'undefined' &&
      !isLocalHostname(window.location.hostname) &&
      isLoopbackApiUrl(envBaseUrl)
    ) {
      // Ignore local-only API URLs accidentally baked into production bundles.
    } else {
      return envBaseUrl;
    }
  }

  if (typeof window !== 'undefined') {
    // Local dev should always go through the Vite proxy to avoid CORS/port confusion,
    // even if the dev server is accessed via a custom hostname (e.g. hosts file mapping).
    if (import.meta.env.DEV) {
      return '/api/booking';
    }

    const hostname = window.location.hostname;

    if (isLocalHostname(hostname)) {
      return 'http://localhost:3003/api';
    }

    // Local production-preview/testing build (custom host, non-default port):
    // use direct local API instead of nginx-only relative path.
    if (!isNexcaveHostname(hostname) || !isDefaultWebPort(window.location.protocol, window.location.port)) {
      return 'http://localhost:3003/api';
    }

    // 生产环境统一走反向代理，避免跨域和域名白名单遗漏
    return '/booking-api/api';
  }

  // SSR / build time fallback
  return import.meta.env.PROD ? '/booking-api/api' : 'http://localhost:3003/api';
};

// 创建axios实例 - 每次请求时动态获取baseURL
const apiClient: AxiosInstance = axios.create({
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let lastRateLimitToastAt = 0;
const RATE_LIMIT_TOAST_COOLDOWN_MS = 15_000;

// 请求拦截器 - 动态设置baseURL和添加认证token
apiClient.interceptors.request.use(
  (config) => {
    // 动态设置baseURL
    config.baseURL = getApiBaseUrl();

    // 添加认证token
    const token = localStorage.getItem('staff_token');
    if (token) {
      config.headers['X-Staff-Token'] = token;
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // 添加请求时间戳用于调试
    (config as { metadata?: { startTime: number } }).metadata = { startTime: Date.now() };

    return config;
  },
  (error) => {
    logger.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器 - 统一处理错误和响应
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // 计算请求耗时
    const endTime = Date.now();
    const startTime =
      (response.config as { metadata?: { startTime: number } }).metadata?.startTime || endTime;
    const duration = endTime - startTime;
    
    if (duration > 1000) {
      logger.warn(`慢请求警告: ${response.config.url} 耗时 ${duration}ms`);
    }

    return response;
  },
  (error) => {
    logger.error('API Error:', error);
    const suppressErrorToast = Boolean((error?.config as AppAxiosConfig | undefined)?.suppressErrorToast);
    if (suppressErrorToast) {
      return Promise.reject(error);
    }
    
    // 处理不同类型的错误
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401: {
          // 认证失败，清除token。避免在登录页或logout请求上触发重定向循环。
          const requestUrl = typeof error.config?.url === 'string' ? error.config.url : '';
          const isLogoutRequest = requestUrl.includes('/staff/logout');
          const isLoginPage =
            typeof window !== 'undefined' && window.location.pathname === '/login';
          localStorage.removeItem('staff_token');
          if (!isLogoutRequest && !isLoginPage) {
            toast.error(i18n.t('auth.authExpired'));
            window.location.href = '/login';
          }
          break;
        }
          
        case 403:
          toast.error(i18n.t('auth.permissionDenied'));
          break;
          
        case 404:
          toast.error(i18n.t('auth.resourceNotFound'));
          break;
          
        case 422:
          // 验证错误，显示具体字段错误
          if (Array.isArray(data.details)) {
            const errorMessages = data.details
              .map((err) => {
                if (typeof err === 'string') return err;
                if (typeof err === 'object' && err !== null) {
                  if ('msg' in err && typeof err.msg === 'string') return err.msg;
                  if ('message' in err && typeof err.message === 'string') return err.message;
                }
                return null;
              })
              .filter((message): message is string => Boolean(message))
              .join(', ');
            toast.error(i18n.t('errors.validationFailed', { errors: errorMessages }));
          } else {
            toast.error(data.error || i18n.t('errors.validationError'));
          }
          break;
          
        case 429:
          if (Date.now() - lastRateLimitToastAt > RATE_LIMIT_TOAST_COOLDOWN_MS) {
            toast.error(i18n.t('errors.rateLimitExceeded'));
            lastRateLimitToastAt = Date.now();
          }
          break;
          
        case 500:
        case 502:
        case 503:
          toast.error(i18n.t('errors.serverError'));
          break;
          
        default:
          toast.error(data.error || i18n.t('errors.requestFailed'));
      }
    } else if (error.request) {
      // 网络错误
      toast.error(i18n.t('errors.networkError'));
    } else {
      // 其他错误
      toast.error(i18n.t('errors.requestProcessingFailed'));
    }
    
    return Promise.reject(error);
  }
);

// 通用API调用函数
async function apiCall<T = unknown>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  endpoint: string,
  data?: unknown,
  config?: AppAxiosConfig
): Promise<ApiResponse<T>> {
  try {
    const response = await apiClient.request({
      method,
      url: endpoint,
      data,
      ...config,
    });

    return {
      data: response.data,
    };
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const message = error.response?.data?.error;
      const details = error.response?.data?.details;
      return {
        error: typeof message === 'string' ? message : i18n.t('errors.requestFailed'),
        details,
        status: error.response?.status,
      };
    }

    return {
      error: error instanceof Error ? error.message : i18n.t('errors.requestFailed'),
    };
  }
}

// 认证相关API
export const authApi = {
  // 员工登录
  login: async (credentials: LoginCredentials): Promise<ApiResponse<{ token: string; user: StaffUser }>> => {
    return apiCall('POST', '/staff/login', credentials);
  },

  // 验证token
  validateToken: async (token: string): Promise<ApiResponse<StaffUser>> => {
    return apiCall('GET', '/staff/me', null, {
      headers: { 'X-Staff-Token': token }
    });
  },

  // 登出
  logout: async (): Promise<ApiResponse> => {
    return apiCall('POST', '/staff/logout');
  },

  // 刷新token
  refreshToken: async (): Promise<ApiResponse<{ token: string }>> => {
    return apiCall('POST', '/staff/refresh-token');
  },

  // 修改当前员工密码
  changePassword: async (data: {
    current_password: string;
    new_password: string;
  }): Promise<ApiResponse> => {
    return apiCall('PUT', '/staff/change-password', data);
  },
};

// 预订相关API
export const bookingApi = {
  // 获取所有预订
  getAll: async (params?: {
    date?: string;
    location_id?: number;
    status?: string;
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<ApiResponse<{ bookings: Booking[]; pagination: PaginationInfo }>> => {
    return apiCall('GET', '/bookings/staff/all', null, { params });
  },

  // 获取预订详情
  getById: async (id: number): Promise<ApiResponse<Booking>> => {
    return apiCall('GET', `/bookings/staff/${id}`);
  },

  // 创建walk-in预订
  createWalkIn: async (data: CreateBookingForm): Promise<ApiResponse<Booking>> => {
    const result = await apiCall('POST', '/bookings/staff/walk-in', data);
    if (result.data && (result.data as { booking?: Booking }).booking) {
      return { data: (result.data as { booking: Booking }).booking };
    }
    return result as ApiResponse<Booking>;
  },

  // 更新预订状态
  updateStatus: async (id: number, data: UpdateBookingStatusForm): Promise<ApiResponse> => {
    return apiCall('PATCH', `/bookings/staff/${id}/status`, data);
  },

  // 更新预订信息
  updateDetails: async (id: number, data: UpdateBookingDetailsForm): Promise<ApiResponse> => {
    return apiCall('PATCH', `/bookings/staff/${id}`, data);
  },

  // 取消预订
  cancel: async (id: number, reason?: string): Promise<ApiResponse> => {
    return apiCall('PATCH', `/bookings/staff/${id}/status`, {
      status: 'cancelled',
      reason
    });
  },

  // 恢复预订（从取消状态）
  restore: async (id: number): Promise<ApiResponse> => {
    return apiCall('PATCH', `/bookings/staff/${id}/status`, {
      status: 'pending'
    });
  },

  // 完成预订
  complete: async (id: number): Promise<ApiResponse> => {
    return apiCall('PATCH', `/bookings/staff/${id}/status`, {
      status: 'completed'
    });
  },

  // 删除预订（仅已取消）
  delete: async (id: number): Promise<ApiResponse> => {
    return apiCall('DELETE', `/bookings/staff/${id}`);
  },
};

// 场地相关API
export const courtApi = {
  // 获取所有场馆
  getLocations: async (): Promise<ApiResponse<Location[]>> => {
    return apiCall('GET', '/courts/locations');
  },

  // 获取可用时间槽
  getAvailableSlots: async (params: {
    date: string;
    location_id?: number;
    duration?: number;
  }): Promise<ApiResponse<TimeSlot[]>> => {
    return apiCall('GET', '/courts/available-slots', null, { params });
  },

  // 获取场地信息
  getCourts: async (location_id?: number): Promise<ApiResponse<Court[]>> => {
    const params = location_id ? { location_id } : undefined;
    return apiCall('GET', '/courts', null, { params });
  },
};

// 支付相关API  
export const paymentApi = {
  // 处理现金支付
  processCashPayment: async (booking_id: number, data: {
    amount: number;
    reference_number?: string;
    notes?: string;
  }): Promise<ApiResponse> => {
    return apiCall('POST', `/payments/cash/${booking_id}`, data);
  },

  // 处理退款
  processRefund: async (booking_id: number, data: {
    amount: number;
    reason: string;
    method: 'cash' | 'original';
  }): Promise<ApiResponse> => {
    return apiCall('POST', `/payments/refund/${booking_id}`, data);
  },

  // 获取支付记录
  getPayments: async (params?: {
    booking_id?: number;
    date_from?: string;
    date_to?: string;
    method?: string;
    status?: string;
  }): Promise<ApiResponse<Payment[]>> => {
    return apiCall('GET', '/payments', null, { params });
  },
};

// 统计分析API
export const analyticsApi = {
  // 获取仪表盘统计数据
  getDashboardStats: async (date_range?: '7days' | '30days' | '90days'): Promise<ApiResponse<DashboardStats>> => {
    const params = date_range ? { date_range } : undefined;
    return apiCall('GET', '/analytics/dashboard', null, { params });
  },

  // 获取收入报表
  getRevenueReport: async (params: {
    date_from: string;
    date_to: string;
    group_by?: 'day' | 'week' | 'month';
    location_id?: number;
  }): Promise<ApiResponse<UnknownRecord[]>> => {
    return apiCall('GET', '/analytics/revenue', null, { params });
  },

  // 获取预订趋势
  getBookingTrend: async (params: {
    date_from: string;
    date_to: string;
    location_id?: number;
  }): Promise<ApiResponse<UnknownRecord[]>> => {
    return apiCall('GET', '/analytics/booking-trend', null, { params });
  },

  // 获取场地利用率
  getCourtUtilization: async (params: {
    date_from: string;
    date_to: string;
    location_id?: number;
  }): Promise<ApiResponse<UnknownRecord[]>> => {
    return apiCall('GET', '/analytics/court-utilization', null, { params });
  },

  // 获取高峰时段分析
  getPeakHours: async (params: {
    date_from: string;
    date_to: string;
    location_id?: number;
  }): Promise<ApiResponse<UnknownRecord[]>> => {
    return apiCall('GET', '/analytics/peak-hours', null, { params });
  },
};

// 客户相关API
export const customerApi = {
  // 搜索客户
  search: async (query: string): Promise<ApiResponse<UnknownRecord[]>> => {
    return apiCall('GET', '/customers/search', null, { params: { q: query } });
  },

  // 获取客户详情
  getById: async (id: number): Promise<ApiResponse<UnknownRecord>> => {
    return apiCall('GET', `/customers/${id}`);
  },

  // 获取客户预订历史
  getBookingHistory: async (id: number, params?: {
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{ bookings: Booking[]; pagination: PaginationInfo }>> => {
    return apiCall('GET', `/customers/${id}/bookings`, null, { params });
  },
};

// WP 客户管理 API（代理 WP REST API）
export const wpCustomersApi = {
  // 获取客户列表
  getList: async (params?: {
    search?: string;
    page?: number;
    per_page?: number;
  }): Promise<ApiResponse<{ users: UnknownRecord[]; pagination: PaginationInfo }>> => {
    return apiCall('GET', '/wp-customers', null, { params });
  },

  // 创建客户账号
  create: async (data: {
    username: string;
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
  }): Promise<ApiResponse<UnknownRecord>> => {
    return apiCall('POST', '/wp-customers', data);
  },

  // 获取客户详情 + 预订历史
  getDetail: async (wpUserId: number, params?: {
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{ user: UnknownRecord; bookings: Booking[]; pagination: PaginationInfo }>> => {
    return apiCall('GET', `/wp-customers/${wpUserId}`, null, { params });
  },

  // 删除客户账号（并将历史 booking 关联置空）
  delete: async (
    wpUserId: number
  ): Promise<ApiResponse<{ message: string; wp_user_id: number; detached_bookings_count: number }>> => {
    return apiCall('DELETE', `/wp-customers/${wpUserId}`);
  },
};

// 员工管理API（仅管理员权限）
export const staffApi = {
  // 获取所有员工
  getAll: async (): Promise<ApiResponse<StaffUser[]>> => {
    return apiCall('GET', '/staff');
  },

  // 获取调试员工列表（开发环境，无需登录）
  getDebugUsers: async (): Promise<ApiResponse<StaffUser[]>> => {
    return apiCall('GET', '/staff/debug-users');
  },

  // 创建员工
  create: async (data: {
    username: string;
    email: string;
    full_name: string;
    password: string;
    role: Role;
    permissions?: Permission[];
    send_welcome_email?: boolean;
    ai_role?: string;
    ai_scopes?: string[];
    ai_enabled?: boolean;
  }): Promise<ApiResponse<StaffUser>> => {
    return apiCall('POST', '/staff', data);
  },

  // 更新员工
  update: async (id: number, data: Partial<StaffUser>): Promise<ApiResponse<StaffUser>> => {
    return apiCall('PUT', `/staff/${id}`, data);
  },

  // 删除员工
  delete: async (id: number): Promise<ApiResponse> => {
    return apiCall('DELETE', `/staff/${id}`);
  },

  // 重置密码
  resetPassword: async (id: number, newPassword: string): Promise<ApiResponse> => {
    return apiCall('POST', `/staff/${id}/reset-password`, { password: newPassword });
  },
};

// 系统设置API
export const settingsApi = {
  // 获取系统设置
  get: async (): Promise<ApiResponse<UnknownRecord>> => {
    return apiCall('GET', '/settings');
  },

  // 更新系统设置
  update: async (settings: UnknownRecord): Promise<ApiResponse> => {
    return apiCall('PUT', '/settings', settings);
  },

  // 获取操作日志
  getLogs: async (params?: {
    action?: string;
    staff_id?: number;
    date_from?: string;
    date_to?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<UnknownRecord[]>> => {
    return apiCall('GET', '/settings/logs', null, { params });
  },
};
// Content Management API
const contentApi = {
  // 获取首页内容
  getHomeContent: async (): Promise<ApiResponse<UnknownRecord>> => {
    return apiCall('GET', '/content/home');
  },

  // 更新首页内容
  updateHomeContent: async (content: UnknownRecord): Promise<ApiResponse> => {
    return apiCall('PUT', '/content/home', content);
  },

  // 获取关于我们内容
  getAboutContent: async (): Promise<ApiResponse<UnknownRecord>> => {
    return apiCall('GET', '/content/about');
  },

  // 更新关于我们内容
  updateAboutContent: async (content: UnknownRecord): Promise<ApiResponse> => {
    return apiCall('PUT', '/content/about', content);
  },

  // 获取教练页面内容
  getCoachingContent: async (): Promise<ApiResponse<UnknownRecord>> => {
    return apiCall('GET', '/content/coaching');
  },

  // 更新教练页面内容
  updateCoachingContent: async (content: UnknownRecord): Promise<ApiResponse> => {
    return apiCall('PUT', '/content/coaching', content);
  },

  // 获取协会页面内容
  getAssociationsContent: async (): Promise<ApiResponse<UnknownRecord>> => {
    return apiCall('GET', '/content/associations');
  },

  // 更新协会页面内容
  updateAssociationsContent: async (content: UnknownRecord): Promise<ApiResponse> => {
    return apiCall('PUT', '/content/associations', content);
  },

  // 获取文章列表
  getArticles: async (): Promise<ApiResponse<UnknownRecord[]>> => {
    return apiCall('GET', '/content/articles');
  },

  // 创建文章
  createArticle: async (article: UnknownRecord): Promise<ApiResponse> => {
    return apiCall('POST', '/content/articles', article);
  },

  // 更新文章
  updateArticle: async (id: number, article: UnknownRecord): Promise<ApiResponse> => {
    return apiCall('PUT', `/content/articles/${id}`, article);
  },

  // 删除文章
  deleteArticle: async (id: number): Promise<ApiResponse> => {
    return apiCall('DELETE', `/content/articles/${id}`);
  },

  // 获取活动列表
  getEvents: async (): Promise<ApiResponse<UnknownRecord[]>> => {
    return apiCall('GET', '/content/events');
  },

  // 创建活动
  createEvent: async (event: UnknownRecord): Promise<ApiResponse> => {
    return apiCall('POST', '/content/events', event);
  },

  // 更新活动
  updateEvent: async (id: number, event: UnknownRecord): Promise<ApiResponse> => {
    return apiCall('PUT', `/content/events/${id}`, event);
  },

  // 删除活动
  deleteEvent: async (id: number): Promise<ApiResponse> => {
    return apiCall('DELETE', `/content/events/${id}`);
  },

  // 获取排名数据
  getRankings: async (): Promise<ApiResponse<UnknownRecord[]>> => {
    return apiCall('GET', '/content/rankings');
  },

  // 批量导入排名
  importRankings: async (data: UnknownRecord[]): Promise<ApiResponse> => {
    return apiCall('POST', '/content/rankings/import', { data });
  },

  // 导出排名数据
  exportRankings: async (): Promise<ApiResponse<UnknownRecord[]>> => {
    return apiCall('GET', '/content/rankings/export');
  },

  // 获取媒体资源汇总
  getMediaAssets: async (): Promise<ApiResponse<UnknownRecord[]>> => {
    return apiCall('GET', '/content/media-assets');
  },

  // 获取文案库
  getCopyLibrary: async (): Promise<ApiResponse<UnknownRecord[]>> => {
    return apiCall('GET', '/content/copy-library');
  },

  // 更新文案库
  updateCopyLibrary: async (content: UnknownRecord): Promise<ApiResponse> => {
    return apiCall('PUT', '/content/copy-library', content);
  },
};

// 通知相关API
export const notificationApi = {
  // 获取通知列表
  getAll: async (params?: {
    unread_only?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<{ notifications: StaffNotification[]; total: number }>> => {
    return apiCall('GET', '/notifications', null, { params });
  },

  // 获取未读数量
  getUnreadCount: async (): Promise<ApiResponse<{ unread_count: number }>> => {
    return apiCall('GET', '/notifications/unread-count');
  },

  // 标记单条为已读
  markAsRead: async (id: number): Promise<ApiResponse> => {
    return apiCall('PATCH', `/notifications/${id}/read`);
  },

  // 全部标记已读
  markAllAsRead: async (): Promise<ApiResponse> => {
    return apiCall('PATCH', '/notifications/read-all');
  },
};

// 排课管理 API
export const schedulingApi = {
  getOverview: async (): Promise<ApiResponse<SchedulingOverview>> => {
    return apiCall('GET', '/scheduling/overview');
  },

  getProgrammes: async (params?: {
    status?: string;
    search?: string;
    date_from?: string; // backward compatible alias
    date_to?: string; // backward compatible alias
    start_date_from?: string;
    start_date_to?: string;
    coach_name?: string;
    capacity_flag?: 'under_min' | 'near_full' | 'full';
    location_id?: number;
  }): Promise<ApiResponse<{ programmes: Programme[] }>> => {
    return apiCall('GET', '/scheduling/programmes', null, { params });
  },

  getProgrammeCalendar: async (params?: {
    start?: string;
    end?: string;
    status?: string;
    search?: string;
    start_date_from?: string;
    start_date_to?: string;
    coach_name?: string;
    capacity_flag?: 'under_min' | 'near_full' | 'full';
    location_id?: number;
  }): Promise<ApiResponse<ProgrammeCalendarResponse>> => {
    return apiCall('GET', '/scheduling/programmes/calendar', null, { params });
  },

  createProgramme: async (data: {
    title: string;
    description?: string | null;
    level?: 'beginner' | 'intermediate' | 'advanced' | 'mixed';
    location_id?: number | null;
    coach_name?: string | null;
    start_date: string;
    end_date: string;
    days_of_week?: number[];
    start_time?: string | null;
    end_time?: string | null;
    min_participants: number;
    max_participants: number;
    content_programme_id?: string | null;
    status?: string;
    notes?: string | null;
  }): Promise<ApiResponse<{ message: string; programme: Programme }>> => {
    return apiCall('POST', '/scheduling/programmes', data);
  },

  getProgrammeDetail: async (programmeId: number): Promise<ApiResponse<{
    programme: Programme;
    sessions: ProgrammeSession[];
    enrollments: ProgrammeEnrollment[];
  }>> => {
    return apiCall('GET', `/scheduling/programmes/${programmeId}`);
  },

  updateProgramme: async (
    programmeId: number,
    data: Partial<Programme>
  ): Promise<ApiResponse<{ message: string; programme: Programme }>> => {
    return apiCall('PUT', `/scheduling/programmes/${programmeId}`, data);
  },

  addProgrammeSession: async (
    programmeId: number,
    data: {
      session_date: string;
      start_time: string;
      end_time: string;
      court_id?: number | null;
      capacity?: number | null;
      notes?: string | null;
    }
  ): Promise<ApiResponse<{ message: string; session: ProgrammeSession }>> => {
    return apiCall('POST', `/scheduling/programmes/${programmeId}/sessions`, data);
  },

  deleteProgrammeSession: async (
    programmeId: number,
    sessionId: number
  ): Promise<ApiResponse<{ message: string }>> => {
    return apiCall('DELETE', `/scheduling/programmes/${programmeId}/sessions/${sessionId}`);
  },

  getProgrammeEnrollments: async (
    programmeId: number,
    params?: { status?: string }
  ): Promise<ApiResponse<{ enrollments: ProgrammeEnrollment[] }>> => {
    return apiCall('GET', `/scheduling/programmes/${programmeId}/enrollments`, null, { params });
  },

  getProgrammeEnrollmentReport: async (
    programmeId: number,
    params?: { format?: 'csv' | 'pdf' | 'both'; status?: ProgrammeEnrollment['status'] | 'all' }
  ): Promise<ApiResponse<{
    message: string;
    programme: { id: number; title: string; code: string };
    reports: Array<{
      format: 'csv' | 'pdf';
      file_name: string;
      storage_url: string;
      public_url: string;
      row_count: number;
    }>;
    report: {
      format: 'csv' | 'pdf';
      file_name: string;
      storage_url: string;
      public_url: string;
      row_count: number;
    } | null;
    total_rows: number;
    generated_at: string;
  }>> => {
    return apiCall('GET', `/scheduling/programmes/${programmeId}/enrollments/report`, null, { params });
  },

  addProgrammeEnrollment: async (
    programmeId: number,
    data: {
      customer_name: string;
      customer_email?: string | null;
      customer_phone?: string | null;
      status?: 'active' | 'waitlisted';
      source?: 'staff' | 'assistant' | 'import' | 'self_service';
      auto_waitlist?: boolean;
    }
  ): Promise<ApiResponse<{ message: string; enrollment: ProgrammeEnrollment }>> => {
    return apiCall('POST', `/scheduling/programmes/${programmeId}/enrollments`, data);
  },

  updateProgrammeEnrollment: async (
    programmeId: number,
    enrollmentId: number,
    data: {
      status?: ProgrammeEnrollment['status'];
      exit_reason?: string | null;
    }
  ): Promise<ApiResponse<{ message: string; enrollment: ProgrammeEnrollment }>> => {
    return apiCall('PATCH', `/scheduling/programmes/${programmeId}/enrollments/${enrollmentId}`, data);
  },

  deleteProgrammeEnrollment: async (
    programmeId: number,
    enrollmentId: number
  ): Promise<ApiResponse<{
    message: string;
    enrollment: {
      id: number;
      status: ProgrammeEnrollment['status'];
      customer_name: string;
    };
  }>> => {
    return apiCall('DELETE', `/scheduling/programmes/${programmeId}/enrollments/${enrollmentId}`);
  },

  getPrivateLessons: async (params?: {
    status?: string;
    search?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<ApiResponse<{ private_lessons: PrivateLesson[] }>> => {
    return apiCall('GET', '/scheduling/private-lessons', null, { params });
  },

  createPrivateLesson: async (data: {
    title?: string;
    customer_name: string;
    customer_email?: string | null;
    customer_phone?: string | null;
    participant_limit?: number;
    date: string;
    start_time?: string | null;
    end_time?: string | null;
    is_flexible_time?: boolean;
    preferred_window_start?: string | null;
    preferred_window_end?: string | null;
    location_id?: number | null;
    court_id?: number | null;
    coach_name?: string | null;
    status?: PrivateLesson['status'];
    price?: number | null;
    notes?: string | null;
  }): Promise<ApiResponse<{ message: string; private_lesson: PrivateLesson }>> => {
    return apiCall('POST', '/scheduling/private-lessons', data);
  },

  getPrivateLessonDetail: async (
    lessonId: number
  ): Promise<ApiResponse<{ private_lesson: PrivateLesson; participants: PrivateLessonParticipant[] }>> => {
    return apiCall('GET', `/scheduling/private-lessons/${lessonId}`);
  },

  updatePrivateLesson: async (
    lessonId: number,
    data: Partial<PrivateLesson>
  ): Promise<ApiResponse<{ message: string; private_lesson: PrivateLesson }>> => {
    return apiCall('PUT', `/scheduling/private-lessons/${lessonId}`, data);
  },

  addPrivateLessonParticipant: async (
    lessonId: number,
    data: {
      participant_name: string;
      participant_email?: string | null;
      participant_phone?: string | null;
    }
  ): Promise<ApiResponse<{ message: string; participant: PrivateLessonParticipant }>> => {
    return apiCall('POST', `/scheduling/private-lessons/${lessonId}/participants`, data);
  },

  updatePrivateLessonParticipant: async (
    lessonId: number,
    participantId: number,
    data: {
      status?: PrivateLessonParticipant['status'];
      participant_name?: string;
      participant_email?: string | null;
      participant_phone?: string | null;
    }
  ): Promise<ApiResponse<{ message: string; participant: PrivateLessonParticipant }>> => {
    return apiCall('PATCH', `/scheduling/private-lessons/${lessonId}/participants/${participantId}`, data);
  },
};

// 导出所有API
export const api = {
  auth: authApi,
  bookings: bookingApi,
  courts: courtApi,
  payments: paymentApi,
  analytics: analyticsApi,
  customers: customerApi,
  wpCustomers: wpCustomersApi,
  staff: staffApi,
  settings: settingsApi,
  content: contentApi,
  notifications: notificationApi,
  scheduling: schedulingApi,
};

export default api;
